import { useCallback, useContext, useEffect, useState } from "react";

import { Form as FormAtd, message } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ActionButton } from "@hdi-seguros/web-components.action-button";
import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  InputCurrencyForm,
  InputForm,
  InputNumberForm,
  RowSize,
  SelectForm,
} from "../../../../../../../shared/components";
import { UploadFileModal } from "./components/UploadFileModal";

import { StepThreeFormContext } from "../../contexts/StepThreeForm";
import { GenericDataFormContext } from "../../../../shared/contexts/GenericDataForm";

import { PaymentMethodFormValuesInput } from "../../interfaces";

import {
  displayErrorMessage,
  ValidateForm,
} from "../../../../../../../shared/utils";
import { PAYMENT_METHODS } from "../../../../shared/utils/methods-payment.enum";

import { useFileHandler } from "../../hooks/useFileHandler";
import { useAddPaymentHandler } from "../../hooks/useAddPaymentHandler";
import { useFileNameHandler } from "../../hooks/useFileNameHandler";

import "./index.css";

export const PaymentMethodSection = () => {
  const validateForm = new ValidateForm();

  const [canShowModal, setCanShowModal] = useState<boolean>(false);

  const { fileSelected, setFileSelected, addPaymentSelected } =
    useAddPaymentHandler();

  const { setFileNameByPaymentMethod } = useFileNameHandler();

  const {
    stepThreePaymentMethodForm,
    setPaymentMethodFormValues,
    getPaymentMethodsOptions,
    getBanksOptions,
    banksOptions,
    paymentMethods,
    calculatedTotals,
    getPaymentList,
    getAllBanksOptions,
    paymentsToLoad,
    allBanksOptions
  } = useContext(StepThreeFormContext);
  const paymentSelected = FormAtd.useWatch(
    "paymentMethod",
    stepThreePaymentMethodForm
  );

  const exactAmount = FormAtd.useWatch(
    "exactAmount",
    stepThreePaymentMethodForm
  );

  const bank = FormAtd.useWatch(
    "bank",
    stepThreePaymentMethodForm
  );

  const transferDate = FormAtd.useWatch(
    "transferDate",
    stepThreePaymentMethodForm
  );

  const checkNumber = FormAtd.useWatch(
    "checkNumber",
    stepThreePaymentMethodForm
  );

  const checkDate = FormAtd.useWatch(
    "checkDate",
    stepThreePaymentMethodForm
  );

  const proofOfPayment = FormAtd.useWatch(
    "proofOfPayment",
    stepThreePaymentMethodForm
  );

  const { openFile, handlerUpload } = useFileHandler(
    stepThreePaymentMethodForm
  );

  const { totalRemainder } = calculatedTotals;

  const { setCanShowSpinner } = useContext(GenericDataFormContext);

  const areAllFieldsFilled = useCallback(() => {
    const paymentMethod = Number(paymentSelected);
    
    if (!paymentMethod || paymentMethod === 0) {
      return false;
    }
    if (!exactAmount) {
      return false;
    }
    if (
      (paymentMethod === PAYMENT_METHODS.BANK_DEPOSIT ||
        paymentMethod === PAYMENT_METHODS.ELECTRONIC_CHECK) &&
      !bank
    ) {
      return false;
    }
    if (
      (paymentMethod === PAYMENT_METHODS.BANK_DEPOSIT ||
        paymentMethod === PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION) &&
      !transferDate
    ) {
      return false;
    }
    if (paymentMethod === PAYMENT_METHODS.ELECTRONIC_CHECK) {
      if (!checkNumber || !checkDate) {
        return false;
      }
    }

    return true;
  }, [
    paymentSelected,
    exactAmount,
    bank,
    transferDate,
    checkNumber,
    checkDate
  ]);

  const handlerSelectPaymentMethod = (
    formValues: PaymentMethodFormValuesInput
  ) => {
    try {
      if (totalRemainder === 0) {
        stepThreePaymentMethodForm?.resetFields(["paymentMethod"]);

        setPaymentMethodFormValues((prevValues) => ({
          ...prevValues,
          ...formValues,
        }));

        displayErrorMessage(
          "No hay saldo a cubrir. No se puede seleccionar una forma de pago."
        );

        return;
      }

      if (formValues.hasOwnProperty("paymentMethod")) {
        stepThreePaymentMethodForm?.resetFields([
          "exactAmount",
          "bank",
          "transferDate",
          "checkNumber",
          "checkDate",
          "observations",
          "proofOfPayment",
        ]);
        setFileSelected([]);
      }

      setPaymentMethodFormValues((prevValues) => ({
        ...prevValues,
        ...formValues,
      }));
    } catch (error) {}
  };

  const onPressAddPaymentMethod = useCallback(async () => {
    if (totalRemainder === 0) {
      message.error(
        "No hay saldo a cubrir. No se puede seleccionar una forma de pago."
      );

      return;
    }

    try {
      await addPaymentSelected();      
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [getPaymentList, addPaymentSelected, setCanShowSpinner]);
  
  useEffect(() => {
    const currentLength = paymentsToLoad.length;
    if (currentLength > 0) {
      getPaymentList(paymentsToLoad);
    }
  }, [paymentsToLoad.length]);

  useEffect(() => {
    getPaymentMethodsOptions();
  }, [getPaymentMethodsOptions]);

  useEffect(() => {
    getBanksOptions();
    getAllBanksOptions();
  }, [getBanksOptions, getAllBanksOptions]);

  useEffect(() => {
    if (!fileSelected?.[0]) {
      stepThreePaymentMethodForm?.setFieldValue("proofOfPayment", null);
    }
  }, [fileSelected]);

  return (
    <Form
      formProps={{
        form: stepThreePaymentMethodForm,
        name: "payment-method-form",
        onFinish: onPressAddPaymentMethod,
        onValuesChange: handlerSelectPaymentMethod,
      }}
    >
      <ContentSection title="Forma de Pago">
        <RowSize className="payment-row-preliquidation">
          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: "Forma de Pago:",
                name: "paymentMethod",
                rules:
                  totalRemainder > 0
                    ? validateForm.generateRules({
                        label: "forma de pago",
                        rules: ["required"],
                      })
                    : [],
              }}
              selectProps={{
                loading: paymentMethods?.length === 0,
                disabled: paymentMethods?.length === 0,
                options: paymentMethods,
                showSearch: true,
              }}
            />
          </ColSize>

          {Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod")) >
            0 && (
            <ColSize size={25}>
              <InputCurrencyForm
                formProps={{
                  label: "Monto Exacto",
                  name: "exactAmount",
                  rules: validateForm.generateRules({
                    label: "monto exacto",
                    rules: ["required"],
                  }),
                }}
                inputCurrencyProps={{
                  precision: 2,
                  step: 0.01,
                  stringMode: true,
                  placeholder: "Ingrese un monto",
                  onKeyDown: (e) => {
                    const input = e.currentTarget;
                    const value = input.value;

                    const isComma = e.key === ",";
                    const isMinus = e.key === "-";
                    const isDot = e.key === ".";

                    if (isComma && value.includes(",")) {
                      e.preventDefault();
                    }

                    if (
                      isMinus &&
                      (value.includes("-") || input.selectionStart !== 0)
                    ) {
                      e.preventDefault();
                    }
                    if (isDot) {
                      e.preventDefault();
                      if(!value.includes(",")){
                        const start = input.selectionStart ?? 0;
                        const end = input.selectionEnd ?? 0;
                    
                        const newValue = value.slice(0, start) + "," + value.slice(end);
                        input.value = newValue;
                    
                        input.setSelectionRange(start + 1, start + 1);
                    
                        const event = new Event("input", { bubbles: true });
                        input.dispatchEvent(event);
                      }
                      return;
                    }

                    if (!/[0-9,-]/.test(e.key) && e.key.length === 1) {
                      e.preventDefault();
                    }
                  },
                  formatter: (value: string | number | undefined) => {
                    if (!value) return "";
                    const number = Number(value.toString().replace(",", "."));
                    return new Intl.NumberFormat("es-AR", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }).format(number);
                  },
                  parser: (value: string | undefined) => {
                    if (!value) return "";

                    const cleaned = value.replace(/\./g, "");

                    if ((cleaned.match(/,/g) || []).length > 1) return "";

                    return cleaned.replace(",", ".");
                  },
                }}
              />
            </ColSize>
          )}

          {(Number(
            stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
          ) === PAYMENT_METHODS.BANK_DEPOSIT ||
            Number(
              stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
            ) === PAYMENT_METHODS.ELECTRONIC_CHECK) && (
            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: "Banco:",
                  name: "bank",
                  rules: validateForm.generateRules({
                    label: "banco",
                    rules: ["required"],
                  }),
                }}
                selectProps={{
                  loading: (Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod")) === PAYMENT_METHODS.ELECTRONIC_CHECK ? allBanksOptions : banksOptions)?.length === 0,
                  disabled: (Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod")) === PAYMENT_METHODS.ELECTRONIC_CHECK ? allBanksOptions : banksOptions)?.length === 0,
                  options: Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod")) === PAYMENT_METHODS.ELECTRONIC_CHECK ? allBanksOptions : banksOptions,
                  showSearch: true,
                }}
              />
            </ColSize>
          )}

          {(Number(
            stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
          ) === PAYMENT_METHODS.BANK_DEPOSIT ||
            Number(
              stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
            ) === PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION) && (
            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  label: "Fecha de Transferencia:",
                  name: "transferDate",
                  rules: validateForm.generateRules({
                    label: "fecha de transferencia",
                    rules: ["required", "dateNotAfterToday"],
                  }),
                }}
                datePickerProps={{
                  maxDate: dayjs(),
                  placeholder: dayjs().format("DD/MM/YYYY"),
                }}
              />
            </ColSize>
          )}

          {Number(
            stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
          ) === PAYMENT_METHODS.ELECTRONIC_CHECK && (
            <>
              <ColSize size={25}>
                <InputNumberForm
                  formProps={{
                    label: "Número de Cheque:",
                    name: "checkNumber",
                    rules: validateForm.generateRules({
                      label: "numero de cheque",
                      rules: ["required", "min:1", "integer"],
                    }),
                  }}
                  inputNumberProps={{
                    placeholder: "0",
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <DatePickerForm
                  formProps={{
                    label: "Fecha del Cheque:",
                    name: "checkDate",
                    rules: validateForm.generateRules({
                      label: "fecha del cheque",
                      rules: ["required"],
                    }),
                  }}
                  datePickerProps={{
                    maxDate: dayjs().add(30, "day"),
                    placeholder: dayjs().format("DD/MM/YYYY"),
                  }}
                />
              </ColSize>
            </>
          )}

          {Number(
            stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
          ) === PAYMENT_METHODS.ROUNDING && (
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: "Observaciones:",
                  name: "observations",
                  rules: validateForm.generateRules({
                    label: "observations",
                    rules: [],
                  }),
                }}
                inputTextAreaProps={{
                  rows: 1,
                  placeholder: "Escribe aquí tus observaciones",
                }}
                isTextArea
              />
            </ColSize>
          )}

          {Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod")) >
            0 && (
            <ColSize size={25}>
              <FormAtd.Item
                label="Comprobante de Pago"
                name="proofOfPayment"
              >
                <div className="container-pay-preliquidation">
                  <ActionButton
                    icon={<UploadOutlined />}
                    label="Subir"
                    type="secondary"
                    className="btn-accent-outline-barbuss button-add-payment-method"
                    onClick={() => setCanShowModal(true)}
                  />
                  <ActionButton
                    label={setFileNameByPaymentMethod(fileSelected)}
                    className="file-name"
                    color="default"
                    type="onlyTextBlack"
                    onClick={() =>
                      fileSelected?.[0]
                        ? openFile(fileSelected[0])
                        : setCanShowModal(true)
                    }
                  />
                </div>
              </FormAtd.Item>
            </ColSize>
          )}
          <RowSize rowProps={{ align: "middle", justify: "end" }}>
            <ActionButton
              className={areAllFieldsFilled() ? 'btn-accent-barbuss' : 'btn-accent-outline-barbuss'}
              width={150}
              icon={<PlusOutlined />}
              label="Agregar"
              type="secondary"
              htmlType="submit"
            />
          </RowSize>
        </RowSize>

        <UploadFileModal
          canShowUploadModalUploadFile={canShowModal}
          setCanShowUploadModalUploadFile={setCanShowModal}
          setFileSelect={(file) => {
            handlerUpload(file);
            setFileSelected(file);
            // loadFilesArray(file);
          }}
          openFile={openFile}
          singleFile
        />
      </ContentSection>
    </Form>
  );
};
